/**
 * THE RECIPE RIDES WITH THE EMAIL.
 *
 * Duplicating a past job today loads the delivered HTML into the Studio as one
 * opaque document (`FormView.applyJob` → `getSentEmail` → `onLoadStudioHtml`).
 * That is faithful to the pixels and useless to the guardrails: there are no
 * blocks, so no cap, no counter, no lock and no dynamic field applies to a
 * duplicate. Anything reconstructed from rendered markup is also lossy — a value
 * that only ever existed inside an `href` cannot be read back out of the text
 * (measured 2026-08-20: a same-template scrape lost 3 of the values a manifest
 * carried byte-identically).
 *
 * So the export stamps the recipe INTO the document as an HTML comment: which
 * blocks, in which order, with which field values, off which template and which
 * SFMC markup version. A duplicate reads that comment and rebuilds real blocks.
 *
 * WHY A COMMENT AND NOT A DATABASE ROW
 * ------------------------------------
 * Because the specialist copy-pastes this HTML into Content Builder, and whatever
 * is not in the HTML does not survive that hop. A sibling record would be correct
 * and would also be silently orphaned the first time someone duplicates an email
 * in Content Builder itself. Verified end to end: 27 plain HTML comments survived
 * a real send AND the hosted view-as-web-page (España eblast, 2026-08-20).
 *
 * WHY EVERY HYPHEN IS ESCAPED
 * ---------------------------
 * A user value containing `-->` terminates the comment early and dumps the rest
 * of the JSON into the visible body of the email. That is not hypothetical — 4 of
 * 9 hostile values did it. Escaping `-` as `-` inside the JSON string makes
 * `-->` unrepresentable while keeping the payload valid JSON and smaller than
 * base64 (measured: 33 escapes vs a 33% size penalty).
 *
 * TWO INDEPENDENT RECOVERIES, RUN EVERY TIME
 * ------------------------------------------
 * Per Jack (2026-08-20): the requester never chooses between methods. The
 * manifest fills the fields; `scrapeValues` independently recovers what it can
 * from the delivered markup; `crossCheck` compares them. Agreement is silent.
 * Only a disagreement is surfaced, and the manifest still wins — the scrape is a
 * checksum, not a source.
 *
 * Kept in step with the monolith's `__manBuild` / `__manRead` / `__manApply`.
 */
import type { GuardrailField } from "./guardrails";
import { capOf, clamp } from "./guardrails";
import { echByName, echMeta, isEch, echNameOf, echVersion } from "./echRegistry";
import { echSeedStrip } from "./echDyn";
import { FULL_REGISTRY } from "../email/blocks";
import type { AnyBlockInstance } from "../email/document";

/** bump when the shape changes; a reader that does not know the version refuses it */
export const MANIFEST_V = 1;
const OPEN = "<!--ECH-MANIFEST:";
const CLOSE = "-->";

export interface ManifestBlock {
  /** the SFMC block NAME (`ECH_BlueHeader`) — the durable key, not the ContentID */
  name: string;
  /** the ContentID at export time, recorded so a rename can be detected */
  id?: string;
  /** 1-based position in the document */
  pos: number;
  /** declared field values only — never `__html` */
  data: Record<string, string>;
  /**
   * WHICH FIELDS THE BLOCK DECLARED AT EXPORT TIME.
   *
   * Recorded separately from `data` because `data` omits empty values (so the rebuild
   * takes the block's default), which means "absent from data" cannot distinguish
   * "field did not exist yet" from "field existed and was left blank". Without this
   * list, `field-new` fired for every blank field on every duplicate — measured on the
   * live Coming Soon template: 8 notices on a round trip where NOTHING had changed.
   * A read-out that cries wolf trains the requester to ignore it.
   *
   * Optional: a stamp written before this was added has no `f`, and in that case
   * `field-new` is suppressed entirely. Silence is the right answer when you cannot
   * tell — it is the false alarm that does the damage.
   */
  f?: string[];
}

export interface Manifest {
  v: number;
  /** the template the document was assembled from (`EchPlacement.tpl`) */
  tpl?: string;
  /** the SFMC markup version those blocks were built from (`EchPlacement.ver`) */
  ver?: number;
  /** ISO date the stamp was written — shown in the duplicate's notice list */
  at?: string;
  blocks: ManifestBlock[];
  /** legacy (non-ECH) block types present in the document, in order */
  legacy?: { type: string; pos: number; data: Record<string, unknown> }[];
}

/* ── BUILD ────────────────────────────────────────────────────────────────── */

/**
 * `__html` IS NOT PART OF THE RECIPE.
 *
 * It is the block's SFMC markup, it is 2–20KB per block, and it is the one field
 * that must come from the LIVE block on rebuild — carrying it would pin a
 * duplicate to markup Layne has since changed, which is the exact failure the
 * `ver` field exists to detect. Also dropped: the synthetic picker key (`__echPick`,
 * a control not a value) and anything else double-underscored.
 */
function recipeData(data: Record<string, unknown>, declared: Record<string, GuardrailField>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(declared || {})) {
    if (k.indexOf("__") === 0) continue;
    const v = data ? data[k] : undefined;
    if (v == null || v === "") continue; /* an absent value takes the block's default on rebuild */
    out[k] = String(v);
  }
  return out;
}

export function buildManifest(blocks: AnyBlockInstance[], at?: string): Manifest {
  const man: Manifest = { v: MANIFEST_V, blocks: [], at: at || new Date().toISOString().slice(0, 10) };
  const list = Array.isArray(blocks) ? blocks : [];
  list.forEach((b, i) => {
    if (!b || !b.type) return;
    if (b.__ech && b.__ech.tpl && !man.tpl) { man.tpl = b.__ech.tpl; man.ver = b.__ech.ver; }
    if (isEch(b.type)) {
      const meta = echMeta(b.type);
      const name = (b.__ech && b.__ech.name) || (meta && meta.echName) || echNameOf(b.type);
      if (!name) return;
      const declared = (meta && meta.declared) || {};
      man.blocks.push({
        name,
        id: (meta && meta.sfmc.id) || (b.__ech && b.__ech.id) || undefined,
        pos: i + 1,
        data: recipeData(b.data || {}, declared),
        f: Object.keys(declared).filter((k) => k.indexOf("__") !== 0),
      });
      return;
    }
    /* a legacy registry block (hero / cta / body) carries no SFMC identity, so it
       is recorded as-is. Its data is small and self-contained by construction. */
    (man.legacy || (man.legacy = [])).push({ type: b.type, pos: i + 1, data: { ...(b.data || {}) } });
  });
  return man;
}

/**
 * JSON with every hyphen AND every percent escaped, so neither `-->` nor `%%` can
 * appear in the payload.
 *
 * `-->` is the HTML comment terminator: a user value containing it ends the comment
 * early and dumps the rest of the JSON into the visible body of the email (4 of 9
 * hostile values did exactly that).
 *
 * `%%` IS AMPSCRIPT'S OWN DELIMITER, and this comment is going to live inside an SFMC
 * email. `%%FirstName%%` inside a comment is still substitution as far as SFMC is
 * concerned, and a stray `%%` is the documented cause of a CloudPage failing to compile.
 * A requester writing "50%% off" — or any two adjacent percent signs arriving from a
 * paste — would have handed SFMC something to interpret in the middle of our recipe.
 * Escaping `%` makes `%%` unrepresentable, the same trick as the hyphen, and costs a few
 * bytes on a payload that has no percent signs in the common case.
 *
 * Both are the same rule, and it is the one worth remembering: when smuggling a payload
 * inside a host format, enumerate that host's delimiters and prove user data can never
 * produce one. HTML's is `-->`; SFMC's is `%%`.
 */
export function encodeManifest(man: Manifest): string {
  return JSON.stringify(man)
    .replace(/-/g, "\\u002d")
    .replace(/%/g, "\\u0025");
}

/**
 * Stamp the recipe into a finished document, immediately before `</body>` (or at
 * the end of a shell that has no closing body tag). Idempotent — a second call
 * replaces the first stamp rather than adding a second one, because
 * `renderDocument` runs on every keystroke in preview.
 */
export function stampManifest(html: string, man: Manifest): string {
  try {
    const h = stripManifest(String(html ?? ""));
    if (!man || !Array.isArray(man.blocks)) return h;
    const stamp = OPEN + encodeManifest(man) + CLOSE;
    const i = h.toLowerCase().lastIndexOf("</body>");
    if (i < 0) return h + stamp;
    return h.slice(0, i) + stamp + h.slice(i);
  } catch { return String(html ?? ""); }
}

/** remove any existing stamp (used before re-stamping, and by the size check) */
export function stripManifest(html: string): string {
  const h = String(html ?? "");
  const i = h.indexOf(OPEN);
  if (i < 0) return h;
  const j = h.indexOf(CLOSE, i);
  if (j < 0) return h;
  return h.slice(0, i) + h.slice(j + CLOSE.length);
}

/* ── READ ─────────────────────────────────────────────────────────────────── */

/**
 * Pull the recipe back out of a delivered email. Returns null for anything it
 * cannot fully trust — no stamp, truncated stamp, unparseable JSON, a version it
 * does not know, or a payload whose `blocks` is not an array. A null here is not
 * an error: it is every email built before this shipped, and every email built
 * outside the Studio. The caller falls back to loading the HTML as-is, which is
 * exactly today's behaviour.
 */
export function readManifest(html: string): Manifest | null {
  try {
    const h = String(html ?? "");
    const i = h.indexOf(OPEN);
    if (i < 0) return null;
    const j = h.indexOf(CLOSE, i);
    if (j < 0) return null; /* truncated — Gmail clipped it, or the paste was partial */
    const raw = h.slice(i + OPEN.length, j);
    const man = JSON.parse(raw) as Manifest;
    if (!man || typeof man !== "object") return null;
    if (man.v !== MANIFEST_V) return null;
    if (!Array.isArray(man.blocks)) return null;
    return man;
  } catch { return null; }
}

/** does this document carry a recipe? (cheap — no JSON parse) */
export function hasManifest(html: string): boolean {
  const h = String(html ?? "");
  const i = h.indexOf(OPEN);
  return i > -1 && h.indexOf(CLOSE, i) > -1;
}

/* ── RECONCILE ────────────────────────────────────────────────────────────── */

export type NoticeKind =
  | "block-missing"   /* the block is no longer in SFMC (retired or renamed) */
  | "block-locked"    /* the block became locked; saved values are dropped */
  | "field-gone"      /* the field no longer exists on the block */
  | "field-new"       /* the block gained a field; it takes the block's default */
  | "clamped"         /* the saved value is longer than the current cap */
  | "mismatch"        /* the manifest and the delivered markup disagree */
  | "stale-markup";   /* the block's SFMC markup changed since the original send */

export interface Notice {
  kind: NoticeKind;
  /** the SFMC block name */
  block: string;
  field?: string;
  was?: string;
  now?: string;
  /** one plain sentence for the requester — no jargon, no instruction to choose */
  msg: string;
}

export interface RebuildResult {
  /** ready to drop straight onto the canvas */
  blocks: AnyBlockInstance[];
  notices: Notice[];
  /** blocks the manifest named that could not be placed at all */
  dropped: string[];
}

let seq = 0;
const nextId = (): string => "dup" + Date.now().toString(36) + (++seq).toString(36);

/**
 * Rebuild real blocks from a recipe, against the LIVE registry.
 *
 * Every difference between then and now is resolved here, and resolved in one
 * direction: the live block wins on STRUCTURE (which fields exist, what they cap
 * at, whether it is locked), the manifest wins on CONTENT. That ordering is what
 * makes a duplicate governed — a value saved under an old 100-character cap is
 * clamped to today's 50, rather than sailing through because it arrived by
 * import. Each resolution emits a notice so the requester can see what moved
 * without being asked to decide anything.
 */
export function manifestToBlocks(man: Manifest | null): RebuildResult {
  const out: RebuildResult = { blocks: [], notices: [], dropped: [] };
  if (!man || !Array.isArray(man.blocks)) return out;

  for (const mb of man.blocks) {
    const name = String((mb && mb.name) || "");
    if (!name) continue;
    const meta = echByName(name);
    if (!meta) {
      out.dropped.push(name);
      out.notices.push({
        kind: "block-missing", block: name,
        msg: name + " is no longer in Marketing Cloud, so it could not be added. Ask eMarketing which block replaced it.",
      });
      continue;
    }

    const declared = meta.declared || {};
    const saved = (mb && mb.data) || {};
    /* the LIVE markup and the live defaults — never the saved markup */
    const data: Record<string, unknown> = liveDefaults(meta.key);

    if (meta.locked && Object.keys(saved).length) {
      out.notices.push({
        kind: "block-locked", block: name,
        msg: name + " is locked now — it is the same artwork for every division, so the wording from the original send does not carry over.",
      });
    }

    /* what the block declared THEN — see ManifestBlock.f */
    const then: string[] | null = Array.isArray(mb.f) ? mb.f : null;

    for (const k of Object.keys(saved)) {
      if (!declared[k]) {
        out.notices.push({
          kind: "field-gone", block: name, field: k, was: String(saved[k]),
          msg: "“" + fieldLabel(declared, k) + "” is not on " + name + " any more, so that text was left out.",
        });
        continue;
      }
      const rule = (meta.gr.fields || {})[k];
      const raw = String(saved[k] == null ? "" : saved[k]);
      const cap = capOf(rule);
      if (cap && raw.length > cap) {
        const cut = clamp(raw, rule);
        data[k] = cut;
        out.notices.push({
          kind: "clamped", block: name, field: k, was: raw, now: cut,
          msg: "“" + fieldLabel(declared, k) + "” was " + raw.length + " characters and the limit is now " + cap + ". It has been trimmed — check it reads right.",
        });
        continue;
      }
      data[k] = raw;
    }

    /* A FIELD THE BLOCK HAS GAINED SINCE THE ORIGINAL SEND — measured against what it
       DECLARED then (`f`), not against which values happened to be filled. `saved`
       omits blanks, so keying off it reported every empty field as new. And with no
       `f` on the stamp there is no way to tell, so nothing is reported: an unexplained
       notice is worse than a missing one. */
    if (then) {
      for (const k of Object.keys(declared)) {
        if (then.indexOf(k) > -1) continue;
        out.notices.push({
          kind: "field-new", block: name, field: k, now: String(data[k] ?? ""),
          msg: name + " has a new “" + fieldLabel(declared, k) + "” since the original send. It is filled with the block's own default.",
        });
      }
    }

    /* a SEED field still holding its authored default ("City, State") was never
       touched — same rule as a fresh placement, so old saves rebuild blank too */
    echSeedStrip({ type: meta.key, data });
    out.blocks.push({ id: nextId(), type: meta.key, data });
  }

  /* legacy blocks carry their own data wholesale — nothing to reconcile */
  for (const lb of man.legacy || []) {
    if (!lb || !lb.type) continue;
    out.blocks.push({ id: nextId(), type: lb.type, data: { ...(lb.data || {}) } });
  }

  return out;
}

/**
 * The live block's starting data, including its current `__html`.
 *
 * `def()` is exactly what the palette calls when placing the block, so a rebuild
 * starts from the same state a fresh placement does — the saved values are then
 * written over the top. Going through `def()` rather than reading a cached
 * defaults object is what guarantees a duplicate picks up markup Layne changed
 * this morning.
 */
function liveDefaults(type: string): Record<string, unknown> {
  try {
    const d = FULL_REGISTRY[type];
    return d && typeof d.def === "function" ? (d.def() as Record<string, unknown>) : {};
  } catch { return {}; }
}

function fieldLabel(declared: Record<string, GuardrailField>, k: string): string {
  const f = declared[k];
  return (f && f.label) || k;
}

/* ── THE INDEPENDENT SECOND OPINION ──────────────────────────────────────── */

const unesc = (s: string): string =>
  String(s ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&"); /* last — an escaped `&amp;lt;` must not become `<` */

const reEsc = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * RECOVER FIELD VALUES FROM THE DELIVERED MARKUP, WITHOUT LOOKING AT THE MANIFEST.
 *
 * For each field the live block declares, take the block's own token markup and
 * the literal text either side of that token, then find that surround in the
 * delivered document and read out what sits between. This works on the delivered
 * bytes only — which is the whole point: two recoveries that share a source
 * cannot check each other.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not segment the document per block,
 * because the export's own trimming (`amenTrimHtml`, `dropEmptyHomeRows`) deletes
 * whole regions and a position-based split would then mis-assign every later
 * block. It matches on surrounding text instead, and simply returns nothing for a
 * field whose surround is gone. NOT-RECOVERED IS NOT A DISAGREEMENT — that
 * distinction is the difference between a useful checksum and a wall of false
 * alarms, and it is enforced in `crossCheck`, not here.
 */
export function scrapeValues(html: string, blockName: string): Record<string, string> {
  const found: Record<string, string> = {};
  try {
    const meta = echByName(blockName);
    if (!meta) return found;
    const markup = String(liveDefaults(meta.key).__html || "");
    if (!markup) return found;
    const doc = String(html ?? "");
    const declared = meta.declared || {};

    for (const k of Object.keys(declared)) {
      const tok = declared[k].token || "{{" + k + "}}";
      const at = markup.indexOf(tok);
      if (at < 0) continue;

      /* THE SURROUND MUST BE MARKUP THAT SURVIVED SUBSTITUTION — I.E. IT MUST NOT
         CONTAIN ANOTHER TOKEN. The first version took a flat 60 characters either
         side, which on a three-field block swallowed the NEIGHBOURING token
         (`…{{headline}}</td></tr><tr><td class="s">` for `sub`) and then bailed out
         on its own "adjacent token" guard — so only the first field of every block
         was ever recovered. Cut at the nearest token boundary first, then tidy to a
         tag boundary so the anchor never starts mid-attribute. */
      let pre = markup.slice(Math.max(0, at - 80), at);
      const lastTok = pre.lastIndexOf("}}");
      if (lastTok > -1) pre = pre.slice(lastTok + 2);
      const gt = pre.lastIndexOf(">");
      if (gt > -1 && gt < pre.length - 1) pre = pre.slice(gt);

      let post = markup.slice(at + tok.length, at + tok.length + 80);
      const nextTok = post.indexOf("{{");
      if (nextTok > -1) post = post.slice(0, nextTok);
      /* the shortest anchor that is still a whole tag. `lt > 0` was wrong: a token
         followed immediately by `</td>` gives lt === 0, which left the anchor at the
         full 80 characters and made it fail on any later edit. */
      const lt = post.indexOf("<");
      if (lt > -1) {
        const close = post.indexOf(">", lt);
        post = close > -1 ? post.slice(0, close + 1) : post.slice(0, lt + 1);
      }

      if (pre.length < 6 || post.length < 3) continue; /* too weak to be unique */
      if (pre.indexOf("{{") > -1 || post.indexOf("{{") > -1) continue; /* still ambiguous */

      /* THE ANCHOR MUST BE UNIQUE IN THE DOCUMENT, OR THERE IS NO RECOVERY — ONLY A
         FALSE ALARM. Caught by App.duplicate.test.tsx on a FAITHFUL round trip: on
         ECH_Logo the surround before `{{community}}` also occurs earlier in the block,
         so the match started at the first occurrence and the non-greedy capture ran
         all the way to the next `post` — swallowing two tags and a comment, and
         reporting the email as "changed after the request went in" when nothing had
         changed at all. A checksum that cries wolf is worse than no checksum: the
         requester learns to ignore the read-out, and then misses the real one. */
      if (doc.indexOf(pre) !== doc.lastIndexOf(pre)) continue;

      const m = new RegExp(reEsc(pre) + "([\\s\\S]*?)" + reEsc(post)).exec(doc);
      if (!m) continue;
      /* AND THE CAPTURE MUST BE A VALUE, NOT MARKUP. Every value is written through
         `escML`, which leaves `<` as `&lt;` and emits `<br>` for a newline — so the
         only tag a genuine capture can contain is `<br>`. Anything else means the
         anchor spanned structure, which is a failed recovery, not a disagreement. */
      if (/<(?!br\s*\/?>)/i.test(m[1])) continue;
      const v = unesc(m[1]).trim();
      if (v) found[k] = v;
    }
  } catch { /* a checksum must never break the duplicate it is checking */ }
  return found;
}

/**
 * Compare the two recoveries and return ONLY the disagreements.
 *
 * The manifest is not corrected here. A mismatch means the delivered email says
 * something the recipe does not, which in practice means the HTML was edited
 * after export — in Content Builder, or by hand. That is worth telling the
 * requester about and is not worth guessing at, so the notice names both values
 * and the rebuild keeps the recipe.
 */
export function crossCheck(man: Manifest | null, html: string): Notice[] {
  const out: Notice[] = [];
  if (!man || !Array.isArray(man.blocks)) return out;
  const doc = String(html ?? "");
  if (!doc) return out;

  for (const mb of man.blocks) {
    const name = String((mb && mb.name) || "");
    if (!name) continue;
    const scraped = scrapeValues(doc, name);
    const saved = (mb && mb.data) || {};
    const meta = echByName(name);
    const declared = (meta && meta.declared) || {};

    for (const k of Object.keys(scraped)) {
      const a = String(saved[k] == null ? "" : saved[k]).trim();
      const b = scraped[k];
      if (!a) {
        /* the markup carries a value the recipe does not. Usually the block's own
           default surviving an untouched field — only flag it when it is not. */
        const dflt = String((declared[k] && declared[k].default) || declared[k]?.label || "").trim();
        if (b && b !== dflt) {
          out.push({
            kind: "mismatch", block: name, field: k, was: "", now: b,
            msg: "The sent email shows “" + b + "” for " + fieldLabel(declared, k) + " on " + name + ", but that was not saved with the request. Check it before sending.",
          });
        }
        continue;
      }
      if (norm(a) !== norm(b)) {
        out.push({
          kind: "mismatch", block: name, field: k, was: a, now: b,
          msg: fieldLabel(declared, k) + " on " + name + " was changed after the request went in — the request says “" + a + "”, the email that went out says “" + b + "”. The request's version has been used.",
        });
      }
    }
  }
  return out;
}

/** whitespace-insensitive compare — `escML` turns a newline into `<br>` and the
 *  scrape turns it back, so a round trip can differ by exactly that much */
const norm = (s: string): string => String(s ?? "").replace(/\s+/g, " ").trim().toLowerCase();

/**
 * THE WHOLE DUPLICATE, IN ONE CALL.
 *
 * Per Jack (2026-08-20): "could it not do both as a straight process — the
 * requester will never make any of this decision." So both recoveries run,
 * unconditionally, and the caller gets blocks plus one flat list of things that
 * moved. No mode, no toggle, nothing to pick.
 *
 * `ok:false` means there was no recipe to read — the caller keeps today's
 * behaviour and loads the HTML as an uneditable document.
 */
export interface DuplicateResult extends RebuildResult {
  ok: boolean;
  man: Manifest | null;
}

export function duplicateFromHtml(html: string): DuplicateResult {
  const man = readManifest(html);
  if (!man) return { ok: false, man: null, blocks: [], notices: [], dropped: [] };
  const built = manifestToBlocks(man);
  const notices = built.notices.concat(crossCheck(man, html));
  /* the markup moved on since the original send — surfaced once for the whole
     document, not once per block */
  try {
    const now = echVersion();
    if (man.ver != null && now && now !== man.ver) {
      notices.unshift({
        kind: "stale-markup", block: man.tpl || "this template",
        msg: "The blocks have been updated in Marketing Cloud since this email went out" + (man.at ? " on " + man.at : "") + ". The new versions are being used.",
      });
    }
  } catch { /* version is a nicety */ }
  return { ok: true, man, blocks: built.blocks, notices, dropped: built.dropped };
}
